import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import EmptyStatePrompt from '../../components/EmptyStatePrompt';
import ExecutiveSummaryCard from './components/ExecutiveSummaryCard';
import StrategicCompliancePanel from './components/StrategicCompliancePanel';
import FinancialProjectionsPanel from './components/FinancialProjectionsPanel';
import DepartmentPerformanceGrid from './components/DepartmentPerformanceGrid';
import ExecutiveAlertPanel from './components/ExecutiveAlertPanel';
import QuarterlyTrendChart from './components/QuarterlyTrendChart';
import { employeeService } from '../../services/employeeService';
import { complianceService } from '../../services/complianceService';
import { departmentalBudgetService } from '../../services/departmentalBudgetService';
import { alertService } from '../../services/alertService';
import { billingService } from '../../services/billingService';
import { supabase } from '../../lib/supabase';
import TeaserEditModal from '../../components/TeaserEditModal';
import { useTeaserEdit } from '../../hooks/useTeaserEdit';

const ExecutiveBoardDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    department: '',
    dateRange: { start: '', end: '' },
    employmentType: ''
  });
  const [executiveSummary, setExecutiveSummary] = useState({
    totalWorkforce: 0,
    complianceScore: 0,
    complianceStatus: 'compliant',
    monthlyPayrollKina: 0,
    revenuePerEmployee: 0
  });
  const [quarterlyTrends, setQuarterlyTrends] = useState([]);
  const [complianceMetrics, setComplianceMetrics] = useState([]);
  const [financialData, setFinancialData] = useState([]);
  const [departmentPerformance, setDepartmentPerformance] = useState([]);
  const [criticalAlerts, setCriticalAlerts] = useState([]);
  const [hasEmployees, setHasEmployees] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState('active');
  const [showPaymentPrompt, setShowPaymentPrompt] = useState(false);
  const [editingField, setEditingField] = useState(null);

  const { 
    editingField: teaserEditField, 
    showRedirectModal, 
    startEdit, 
    handleInput: handleTeaserInput, 
    closeModal,
    isEditable 
  } = useTeaserEdit(subscriptionStatus === 'active');

  useEffect(() => {
    loadExecutiveData();
  }, [filters?.department]);

  const loadExecutiveData = async () => {
    try {
      setLoading(true);
      const employeesData = await employeeService?.getAll();
      setHasEmployees(employeesData?.length > 0);

      if (employeesData?.length > 0) {
        const [complianceData, budgetOverview, alertsData] = await Promise.all([
          complianceService?.getComplianceMetrics(),
          departmentalBudgetService?.getBudgetOverview(),
          alertService?.getActiveAlerts()
        ]);

        // Check subscription status
        try {
          const { data: user } = await supabase?.auth?.getUser();
          if (user?.user?.id) {
            const { data: companySettings } = await supabase?.from('company_settings')?.select('id')?.eq('user_id', user?.user?.id)?.single();
            
            if (companySettings?.id) {
              const subscriptionDetails = await billingService?.getSubscriptionDetails(companySettings?.id);
              const status = subscriptionDetails?.subscriptionStatus || 'suspended';
              setSubscriptionStatus(status);
              setShowPaymentPrompt(status !== 'active');
            }
          }
        } catch (subError) {
          console.error('Error checking subscription status:', subError);
          setShowPaymentPrompt(true);
        }

        // Calculate executive summary
        const overallCompliance = complianceData?.[0]?.value || 0;
        const complianceStatus = overallCompliance >= 95 ? 'compliant' : overallCompliance >= 85 ? 'warning' : 'critical';
        
        setExecutiveSummary({
          totalWorkforce: employeesData?.length || 0,
          complianceScore: overallCompliance,
          complianceStatus: complianceStatus,
          monthlyPayrollKina: budgetOverview?.totalPayrollCostKina || 0,
          revenuePerEmployee: budgetOverview?.totalPayrollCostKina ? (budgetOverview?.totalPayrollCostKina * 2.5 / employeesData?.length) : 0
        });

        // Set quarterly trends (mock data structure)
        setQuarterlyTrends([
          { quarter: 'Q1 2025', compliance: 92, workforce: employeesData?.length - 15, payroll: (budgetOverview?.totalPayrollCostKina || 0) * 0.9 },
          { quarter: 'Q2 2025', compliance: 94, workforce: employeesData?.length - 8, payroll: (budgetOverview?.totalPayrollCostKina || 0) * 0.95 },
          { quarter: 'Q3 2025', compliance: 93, workforce: employeesData?.length - 3, payroll: (budgetOverview?.totalPayrollCostKina || 0) * 0.98 },
          { quarter: 'Q4 2025', compliance: overallCompliance, workforce: employeesData?.length, payroll: budgetOverview?.totalPayrollCostKina || 0 }
        ]);

        // Set compliance metrics
        setComplianceMetrics(complianceData || []);

        // Set financial projections
        const budgets = await departmentalBudgetService?.getAllBudgets();
        setFinancialData(budgets || []);

        // Set department performance
        const deptCompliance = await complianceService?.getDepartmentCompliance();
        setDepartmentPerformance(deptCompliance || []);

        // Set critical alerts (filter for high priority)
        const criticalOnly = alertsData?.filter(alert => alert?.severity === 'high' || alert?.severity === 'critical') || [];
        setCriticalAlerts(criticalOnly?.slice(0, 5));
      }
    } catch (error) {
      console.error('Error loading executive dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleDrillDown = (path) => {
    navigate(path);
  };

  const handleExportPresentation = () => {
    try {
      const presentationData = {
        summary: executiveSummary,
        departments: departmentPerformance,
        financials: financialData,
        trends: quarterlyTrends,
        alerts: criticalAlerts
      };

      const jsonStr = JSON.stringify(presentationData, null, 2);
      const blob = new Blob([jsonStr], { type: 'application/json' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link?.setAttribute('href', url);
      link?.setAttribute('download', `executive-board-presentation-${new Date()?.toISOString()?.split('T')?.[0]}.json`);
      link.style.visibility = 'hidden';
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  const handleAddEmployees = () => {
    navigate('/workforce-management-dashboard');
  };

  const formatKina = (amount) => {
    return `K ${amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleTeaserEdit = (field) => {
    setEditingField(field);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16">
          <GlobalFilterBar onFilterChange={handleFilterChange} />
          <ComplianceAlertBanner />
          <DataRefreshIndicator />
          <main className="container mx-auto px-4 py-8">
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center gap-3">
                <Icon name="Loader" size={32} color="var(--color-primary)" className="animate-spin" />
                <p className="text-muted-foreground">Loading executive dashboard...</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (!hasEmployees) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16">
          <GlobalFilterBar onFilterChange={handleFilterChange} />
          <ComplianceAlertBanner />
          <DataRefreshIndicator />
          <main className="container mx-auto px-4 py-8">
            <EmptyStatePrompt
              icon="BarChart3"
              title="No Workforce Data Available"
              description="Add employees to your organization to view executive insights, compliance metrics, and financial analytics."
              actionLabel="Add Employees"
              onAction={handleAddEmployees}
            />
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <GlobalFilterBar onFilterChange={handleFilterChange} />
        <ComplianceAlertBanner />
        <DataRefreshIndicator />
        
        {/* Payment Prompt Banner */}
        {showPaymentPrompt && (
          <div className="bg-warning/10 border-b border-warning/20">
            <div className="container mx-auto px-4 py-4">
              <div className="flex items-start gap-3">
                <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-warning mb-1">
                    Limited Access - Payment Required
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Once payment is made, you will be able to fully utilize this dashboard for your organization's HR Management. Some features are currently view-only until your subscription is activated.
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/payment-contact')}
                  className="flex-shrink-0"
                >
                  Make Payment
                </Button>
              </div>
            </div>
          </div>
        )}
        
        <main className="container mx-auto px-4 py-8">
          {/* Page Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 
                className="font-heading text-2xl md:text-3xl font-bold text-foreground mb-2 relative group"
                contentEditable={isEditable && teaserEditField === 'page-title'}
                suppressContentEditableWarning={true}
                onInput={(e) => handleTeaserInput(e, 'page-title')}
                onClick={() => startEdit('page-title', 'Executive Board Dashboard')}
                style={{ cursor: isEditable ? 'pointer' : 'default' }}
              >
                Executive Board Dashboard
                {isEditable && (
                  <span className="absolute -right-8 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Icon name="Edit2" size={16} color="var(--color-primary)" />
                  </span>
                )}
              </h1>
              <p 
                className="text-sm md:text-base text-muted-foreground relative group"
                contentEditable={isEditable && teaserEditField === 'page-subtitle'}
                suppressContentEditableWarning={true}
                onInput={(e) => handleTeaserInput(e, 'page-subtitle')}
                onClick={() => startEdit('page-subtitle', 'Strategic oversight of organizational performance, compliance, and financial health')}
                style={{ cursor: isEditable ? 'pointer' : 'default' }}
              >
                Strategic oversight of organizational performance, compliance, and financial health
                {isEditable && (
                  <span className="absolute -right-6 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Icon name="Edit2" size={14} color="var(--color-primary)" />
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="md"
                onClick={handleExportPresentation}
                iconName="Download"
                iconPosition="left"
              >
                Export Report
              </Button>
            </div>
          </div>

          {/* Executive Summary Cards - 4 columns */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <ExecutiveSummaryCard
              title="Total Workforce"
              value={executiveSummary?.totalWorkforce}
              subtitle="Active employees"
              icon="Users"
              trend="up"
              status="compliant"
              onClick={() => handleDrillDown('/workforce-analytics')}
              isEditable={isEditable}
              teaserEditField={teaserEditField}
              onStartEdit={startEdit}
              onTeaserInput={handleTeaserInput}
            />
            <ExecutiveSummaryCard
              title="Overall Compliance"
              value={`${executiveSummary?.complianceScore}%`}
              subtitle="PNG Employment Act adherence"
              icon="Shield"
              trend={executiveSummary?.complianceScore >= 95 ? 'up' : 'down'}
              status={executiveSummary?.complianceStatus}
              onClick={() => handleDrillDown('/compliance-overview')}
              isEditable={isEditable}
              teaserEditField={teaserEditField}
              onStartEdit={startEdit}
              onTeaserInput={handleTeaserInput}
            />
            <ExecutiveSummaryCard
              title="Monthly Payroll"
              value={formatKina(executiveSummary?.monthlyPayrollKina)}
              subtitle="Total payroll costs"
              icon="DollarSign"
              trend="up"
              status="compliant"
              onClick={() => handleDrillDown('/payroll-compliance')}
              isEditable={isEditable}
              teaserEditField={teaserEditField}
              onStartEdit={startEdit}
              onTeaserInput={handleTeaserInput}
            />
            <ExecutiveSummaryCard
              title="Revenue per Employee"
              value={formatKina(executiveSummary?.revenuePerEmployee)}
              subtitle="Productivity metric"
              icon="TrendingUp"
              trend="up"
              status="compliant"
              onClick={() => handleDrillDown('/workforce-analytics')}
              isEditable={isEditable}
              teaserEditField={teaserEditField}
              onStartEdit={startEdit}
              onTeaserInput={handleTeaserInput}
            />
          </div>

          {/* Main Analytics Panel - 8 columns */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
            <div className="lg:col-span-8">
              <QuarterlyTrendChart
                data={quarterlyTrends}
                onDrillDown={handleDrillDown}
              />
            </div>
            <div className="lg:col-span-4">
              <ExecutiveAlertPanel
                alerts={criticalAlerts}
                onViewAll={() => handleDrillDown('/compliance-overview')}
              />
            </div>
          </div>

          {/* Strategic Compliance Overview */}
          <div className="mb-8">
            <StrategicCompliancePanel
              metrics={complianceMetrics}
              onDrillDown={handleDrillDown}
            />
          </div>

          {/* Financial Projections */}
          <div className="mb-8">
            <FinancialProjectionsPanel
              data={financialData}
              formatKina={formatKina}
              onDrillDown={handleDrillDown}
            />
          </div>

          {/* Department Performance Grid */}
          <div>
            <DepartmentPerformanceGrid
              departments={departmentPerformance}
              onDrillDown={handleDrillDown}
            />
          </div>
        </main>
      </div>
      
      <TeaserEditModal isOpen={showRedirectModal} onClose={closeModal} />
    </div>
  );
};

export default ExecutiveBoardDashboard;